import { isAxiosError } from 'axios';
import type { QueryClient } from '@tanstack/react-query';

// ─── Cache invalidation ───────────────────────────────────────────────────────

/**
 * URL prefixes whose matching queries must be refetched when a run starts or a
 * change decision lands.  React Query prefix-matches the first key element, so
 * a single base URL covers the list page, every detail page and the nested
 * requirements page in one go.
 *
 * Groups:   /api/requirements/v1.0/groups/<uuid>/
 *           /api/requirements/v1.0/groups/<uuid>/requirements/
 * Changes:  /api/benefit-configuration/v1.0/changes/<uuid>/
 */
const RUN_AFFECTED_PREFIXES = [
  '/api/requirements/v1.0/groups/',
  '/api/benefit-configuration/v1.0/changes/',
] as const;

/**
 * Bust every requirements-workspace cache entry so a run start or decision
 * reflects immediately without waiting for the next poll tick.
 */
export const invalidateWorkspaceCaches = (queryClient: QueryClient) => {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const [first] = query.queryKey;
      return (
        typeof first === 'string' &&
        RUN_AFFECTED_PREFIXES.some((prefix) => first.startsWith(prefix))
      );
    },
  });
};

// ─── Error messages ───────────────────────────────────────────────────────────

/**
 * The API's `detail` string, or the fallback when the failure carries no body
 * — a network error, or anything that never reached the endpoint.
 */
export const apiErrorMessage = (error: unknown, fallback: string) =>
  (isAxiosError<{ detail?: string }>(error)
    ? error.response?.data?.detail
    : undefined) ?? fallback;
